/* eslint-disable camelcase */
import React from "react"
import { useLocation } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { Box, Paper, TextField, Grid, Typography, Divider, LinearProgress, Card, CardContent } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { api, showWarning } from "../../../api"
import normalFormatDate from "../../utils/date/DDMMYYYFormatDate"

// Íconos para la interfaz visual
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd"
import AnalyticsIcon from "@mui/icons-material/Analytics"
import PersonIcon from "@mui/icons-material/Person"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const getEstadoLegible = (status) => {
  const statusMap = {
    G: "GENERADO",
    P: "EN PROCESO",
    C: "CERRADO",
    F: "FINALIZADO",
    X: "ANULADO",
  }
  return statusMap[status] || status
}

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value || 0)
}

const BuscarTomaFisica = () => {
  const location = useLocation()
  const tomnumero_state = location.state?.tomnumero || ""

  if (!tomnumero_state) {
    showWarning("No se especificó un número de tomo para consultar.")
  }

  const { data: tomoData, isLoading } = useQuery({
    queryKey: ["getTomoCabecera", tomnumero_state],
    queryFn: async () => {
      try {
        const response = await api.post("/TomaFisica/getTomoCabecera", {
          tomnumero: tomnumero_state,
        })
        return response?.data?.data || response?.data || {}
      } catch (error) {
        console.error("Error al cargar la información del tomo:", error)
        return {}
      }
    },
    enabled: !!tomnumero_state,
    refetchOnWindowFocus: false,
  })

  const infoTomo = tomoData || {}
  const operarios = Array.isArray(infoTomo.operarios) ? infoTomo.operarios : []

  const totalItems = Number(infoTomo.total_items) || 0
  const contados = Number(infoTomo.items_contados) || 0
  const conDiferencia = Number(infoTomo.items_diferencia) || 0

  const pctAvance = totalItems > 0 ? (contados / totalItems) * 100 : 0
  const pctDiferencia = totalItems > 0 ? (conDiferencia / totalItems) * 100 : 0

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Consulta de Tomo Físico y Rendimiento</b>
        </div>

        <CustomBackdrop isLoading={isLoading} />

        <Box sx={StyledRoot}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3, background: "white" }}>
            {/* SECCIÓN 1: CABECERA BASE */}
            <Typography variant="h6" color="primary" gutterBottom sx={{ mb: 3, fontWeight: "bold" }}>
              Información del Inventario Planificado
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Número de Tomo"
                  value={infoTomo.tomnumero || ""}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Estado Actual"
                  value={getEstadoLegible(infoTomo.tomstatus)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Tipo de Toma"
                  value={infoTomo.tomtipo || ""}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Bodega"
                  value={infoTomo.bodcodigo || ""}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción General"
                  value={infoTomo.tomdescripcion || ""}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Filtro por Marca"
                  value={infoTomo.marcodigo ? `${infoTomo.marcodigo} - ${infoTomo.mardescri || ""}` : "NO APLICA"}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Filtro por Familia / Línea"
                  value={infoTomo.lincodigo ? `${infoTomo.lincodigo} - ${infoTomo.lindescri || ""}` : "NO APLICA"}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* SECCIÓN 2: KPIs CONSOLIDADOS (TÍTULO ACTUALIZADO) */}
            <Typography
              variant="h6"
              color="primary"
              gutterBottom
              sx={{ mb: 2, display: "flex", alignItems: "center", fontWeight: "bold" }}
            >
              <AnalyticsIcon sx={{ mr: 1 }} /> Rendimiento Global y Diferencias (Consolidado)
            </Typography>

            <Grid container spacing={4} mb={2}>
              <Grid item xs={12} md={6}>
                <Box bgcolor="#f0fdf4" p={2} borderRadius={2} border="1px solid #c8e6c9">
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Avance de Conteo General (<b>{pctAvance.toFixed(1)}%</b>)
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={pctAvance}
                    color="success"
                    sx={{ height: 12, borderRadius: 5, mb: 1 }}
                  />
                  <Typography variant="body2" fontWeight="bold" color="success.main">
                    Se han contado {contados} de {totalItems} ítems planificados.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box bgcolor="#fff3e0" p={2} borderRadius={2} border="1px solid #ffcc80">
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Índice de Diferencias (<b>{pctDiferencia.toFixed(1)}%</b>)
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={pctDiferencia}
                    color="warning"
                    sx={{ height: 12, borderRadius: 5, mb: 1 }}
                  />
                  <Typography variant="body2" fontWeight="bold" color="warning.main">
                    {conDiferencia} ítems presentan faltantes o sobrantes.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Impacto en Dólares (Sobrantes Totales)"
                  value={formatCurrency(infoTomo.valor_sobrantes)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true, style: { color: "#2E7D32", fontWeight: "bold" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Impacto en Dólares (Faltantes Totales)"
                  value={formatCurrency(infoTomo.valor_faltantes)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true, style: { color: "#d32f2f", fontWeight: "bold" } }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* SECCIÓN 3: AVANCE POR OPERARIO (NUEVA VISTA DETALLADA) */}
            <Typography
              variant="h6"
              color="primary"
              gutterBottom
              sx={{ mb: 2, display: "flex", alignItems: "center", fontWeight: "bold" }}
            >
              <AssignmentIndIcon sx={{ mr: 1 }} /> Avance de Captura Móvil por Operario
            </Typography>

            <Grid container spacing={3} mb={4}>
              {operarios.length > 0 ? (
                operarios.map((op, idx) => {
                  const pctOperario = totalItems > 0 ? (Number(op.contados) / totalItems) * 100 : 0
                  return (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <Card
                        variant="outlined"
                        sx={{ borderRadius: 2, borderColor: "#e0e0e0", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
                      >
                        <CardContent sx={{ pb: "16px !important" }}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <PersonIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="subtitle2" fontWeight="bold" color="textPrimary" noWrap>
                              {op.nombre ? `${op.codigo} - ${op.nombre}` : op.codigo}
                            </Typography>
                          </Box>

                          <Box mt={2}>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography variant="caption" color="textSecondary" fontWeight="bold">
                                Progreso Individual
                              </Typography>
                              <Typography variant="caption" color="primary" fontWeight="bold">
                                {pctOperario.toFixed(1)}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={pctOperario}
                              sx={{ height: 8, borderRadius: 4, bgcolor: "#e0f2f1" }}
                            />
                            <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                              Ha escaneado <b>{op.contados}</b> ítems únicos de {totalItems}.
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )
                })
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary" sx={{ p: 2, bgcolor: "#f5f7fa", borderRadius: 2 }}>
                    No hay personal asignado o es un inventario estrictamente manual.
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* SECCIÓN 4: AUDITORÍA BASE */}
            <Typography variant="h6" color="primary" gutterBottom sx={{ mb: 3, fontWeight: "bold" }}>
              Datos de Creación y Auditoría
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Usuario Creador"
                  value={infoTomo.tomusuisys || infoTomo.tomusuario || ""}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Fecha de Creación"
                  value={infoTomo.tomfecisys ? normalFormatDate(infoTomo.tomfecisys) : ""}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Última Modificación"
                  value={infoTomo.tomusumsys || ""}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default BuscarTomaFisica
